import React, { useContext, useEffect, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';

import Header from './Header';
import Sidebar from './Sidebar';

import { GlobalContext } from '../../store/Provider';

/**
 * App shell for the authenticated pages.
 *
 * Header and Sidebar used to be rendered by every route component. Inside a
 * Switch each route is its own subtree, so navigating unmounted the Header and
 * mounted a fresh one, which refired the whole fetchData() bootstrap (~6 API
 * calls) on every page change and restarted the polling intervals. Rendering
 * the shell once around the inner Switch keeps both mounted for the session.
 *
 * The page markup lives here too: the sidebar and header styling hang off the
 * data attributes on #main-wrapper, so the wrapper has to be the same element
 * that stays mounted.
 */
const Layout = ({ children }) => {
  const { authState, layoutState } = useContext(GlobalContext);
  const { pathname } = useLocation();

  // The mobile menu button toggles the show-sidebar class straight onto
  // #main-wrapper. That used to be wiped every navigation because the wrapper
  // was rebuilt with the page; now it survives, so the off-canvas sidebar
  // would stay open on top of the page the user just navigated to.
  useEffect(() => {
    const wrapper = document.getElementById('main-wrapper');
    if (wrapper) {
      wrapper.classList.remove('show-sidebar');
    }
  }, [pathname]);

  // getSidebarType() reads window.innerWidth, which is not reactive, and this
  // component no longer re-renders on navigation. Track the breakpoint so
  // resizing or rotating the device still switches the sidebar type.
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // The shell renders above PrivateRoute/OwnerRoute and Header reads
  // authState.user straight away, so the authentication check has to be
  // repeated here. The permission checks stay on the individual routes.
  if (!authState.isAuthenticated) {
    return <Redirect to='/' />;
  }

  const getSidebarType = () => {
    if (isDesktop) {
      return layoutState.sidebarOpen ? 'iconbar' : 'mini-sidebar';
    } else {
      return 'mini-sidebar';
    }
  };

  return (
    <div
      id='main-wrapper'
      data-layout='vertical'
      data-sidebartype={getSidebarType()}
      data-sidebarposition='fixed'
      data-header-position='fixed'
      data-boxed-layout='full'
      data-theme='light'
    >
      <Header />
      <Sidebar />
      <div className='page-wrapper d-block'>
        <div className='page-content container-fluid'>{children}</div>
      </div>
      {/* Footer */}
    </div>
  );
};

export default Layout;
